<script setup>
   import { onMounted, ref, watch, watchEffect, inject } from 'vue'
   import { RouterLink, useRoute } from 'vue-router'
   import { modalHide, modalShow } from '@/composables/bootstrap'
   import NewCollection from '@/components/Collection/NewCollection.vue'
   import DeleteCollection from '@/components/Collection/DeleteCollection.vue'
   import EditCollection from '@/components/Collection/EditCollection.vue'
   import { useElementVisibility } from '@vueuse/core'
   import { useEnvironment } from '@/config'
   const environment = useEnvironment()

   const route = useRoute()

   const $storeCollections = inject('$storeCollections')
   const $storeGinnels = inject('$storeGinnels')
   const $storeApp = inject('$storeApp')
   const $storeNavigation = inject('$storeNavigation')

   onMounted(() => $storeCollections.get())

   const currentPage = ref(1)
   watchEffect(() => currentPage.value = $storeCollections.currentPage)
   const hasMoreData = ref(true)
   watchEffect(() => hasMoreData.value = $storeCollections.hasMoreData)
   const gotCollections = ref(false)
   watchEffect(() => gotCollections.value = $storeCollections.gotCollections)

   /**
      * watch: targetIsVisible.value
      * @description Watches if the element comes in to the viewport.
      * Then sends a request to get more data.
   */
   const target = ref(null)
   const targetIsVisible = useElementVisibility(target)
   const loadingData = ref(false)
   watch(() => targetIsVisible.value, isVisible => {

      if(isVisible && hasMoreData.value && ! loadingData.value) {
         loadingData.value = true

         setTimeout(() => {
            $storeCollections.get({ page: parseInt(currentPage.value) + 1 })
            loadingData.value = false
         }, 600)
      }
   })

   const collections = ref([])
   watchEffect(() =>  collections.value = $storeCollections.collections)

   // Hide the modal after a collection is added or deleted
   watch(() => collections.value, newData => modalHide('collection-modal'), { deep: true })

   // Get an array of ginnels
   const getGinnels = collection_id => $storeGinnels.get({ collection_id: collection_id })

   /**
      * Function: callModal
      * @see <NewCollection>
      * @see <DeleteCollection>
      * @description This function opens the modal, sets the action
      * to the modal's ID, and sets the selectedCollection.
      *
      * @see ModalAction is a ref that holds the modal ID, used solely as a prop.
      * @see selectedCollection is a ref that holds the collection object, used solely as a prop.
   */
   const modalAction = ref('')
   const selectedCollection = ref(null)
   const callModal = (action, targetObject = null) => {
      if(targetObject) selectedCollection.value = targetObject
      modalAction.value = action
      setTimeout(() => modalShow(action), 50)
   }

   /**
      * Function: setLinkHoverState
      * @description This sets the hover state for the
      * collection navigation items. It shows the edit
      * and delete buttons.
      *
      * @param {String or Number} collection_id
   */
   const collectionLinkState = ref(null)
   const setLinkHoverState = (collection_id = null) => collectionLinkState.value = collection_id

   const iconButtonState = ref(null)
   const setIconButtonState = (icon = null) => iconButtonState.value = icon

   /*
   |--------------------------------------------------------------------------
   | Navigation Status
   |--------------------------------------------------------------------------
   */
   const openNav = navID => $storeNavigation.openNav(navID)
   const closeNav = navID => $storeNavigation.closeNav(navID)
</script>

<template>
   <div class="cg-nav-button-icons mb-4">
      <!-- Explore Link -->
      <RouterLink
         :to="{ name: 'home' }"
         @mouseover="setIconButtonState('dashboard')"
         @mouseleave="setIconButtonState()"
         class="cg-row-content gap-2">

         <span class="nav-button-icon" :class="{ 'text-white': $route.name == 'home' }">
            <FontAwesomeIcon :icon="`${iconButtonState == 'dashboard'}fa-solid fa-dashboard`" />
         </span>
         <span  :class="{ 'text-white': $route.name == 'home' }">
            Dashboard
         </span>
      </RouterLink>

      <!-- Saved Link -->
      <!-- <RouterLink
         :to="{ name: 'bookmarks' }"
         @mouseover="setIconButtonState('bookmarks')"
         @mouseleave="setIconButtonState()"
         class="nav-button-icon" :class="{ 'text-white': $route.name == 'bookmarks' }" v-tippy title="Saved">

         <FontAwesomeIcon :icon="`${iconButtonState == 'bookmarks' || $route.name == 'bookmarks' ? 'fa-solid' : 'fa-regular'} fa-bookmark`" />
      </RouterLink> -->

      <!-- Tags Link -->
      <RouterLink
         :to="{ name: 'tags' }"
         @mouseover="setIconButtonState('tags')"
         @mouseleave="setIconButtonState()"
         class="cg-row-content gap-2">

         <span class="nav-button-icon" :class="{ 'text-white': $route.name == 'tags' }">
            <FontAwesomeIcon :icon="`${iconButtonState == 'tags' || $route.name == 'tags' ? 'fa-solid fa-tags' : 'fa-solid fa-tag'}`" />
         </span>
         <span  :class="{ 'text-white': $route.name == 'tags' }">
            Tags
         </span>
      </RouterLink>

      <!-- Followers Link -->
      <!-- <RouterLink
         :to="{ name: 'followers' }"
         @mouseover="setIconButtonState('followers')"
         @mouseleave="setIconButtonState()"
         class="nav-button-icon" :class="{ 'text-white': $route.name == 'followers' }">

         <FontAwesomeIcon :icon="`${iconButtonState == 'followers' || $route.name == 'followers' ? 'fa-solid fa-people-group' : 'fa-solid fa-people-group'}`" />
      </RouterLink> -->
   </div>

   <div class="collections h-100 mb-3 mb-sm-0">
      <!-- List of Collections -->
      <nav>

            <div class="selector-heading d-flex justify-content-between w-100">
               <div class="nav-heading">
                  <HelpTooltip tooltip="A Collection is a group of Ginnels, allowing you to organize and manage multiple Ginnels together." />
                  Collections
               </div>

               <div class="collection-action-buttons">
                  <div @click="callModal('collection-modal')" class="add-button">
                     <FontAwesomeIcon icon="fas fa-plus" />
                  </div>
               </div>
            </div>

            <div v-if="! collections?.length && gotCollections" class="alert alert-dark mt-3">
               <p>It looks like you don't have any collections yet.</p>
               <span @click="callModal('collection-modal')" class="link d-flex align-items-center gap-2">
                  <FontAwesomeIcon class="body-text-icon" icon="a-solid fa-layer-group" /> Get Started.
               </span>
            </div>

            <template v-for="collection in collections" :key="collection.id">
               <RouterLink
                  :to="{ name: 'collection', params: { collection_id: collection.id } }"
                  @click="getGinnels(collection.id); openNav('ginnels'); closeNav('collections')"
                  @mouseover="setLinkHoverState(collection.id)"
                  @mouseleave="setLinkHoverState()"
                  class="collections-link">

                  <div class="d-flex align-items-center gap-2">
                     <FontAwesomeIcon icon="fas fa-layer-group" /> {{ collection.name }}
                  </div>

                  <div v-if="collectionLinkState == collection.id" class="d-flex gap-2 body-text-icon">
                     <FontAwesomeIcon @click="callModal('edit-collection', collection)" icon="fas fa-pencil-alt" class="text-warning" />
                     <FontAwesomeIcon @click="callModal('delete-collection', collection)" icon="fas fa-trash-alt" class="text-danger" />
                  </div>
                  <span v-else class="badge bg-primary">{{ collection.ginnels }}</span>
               </RouterLink>
            </template>

            <div v-if="loadingData || ! gotCollections" class="cg-row-content justify-content-center pt-2">
               <Loader size="40" />
            </div>

            <div v-if="collections?.length" ref="target" class="p-3"></div>

      </nav>
   </div>

   <NewCollection :action="modalAction" />
   <DeleteCollection v-if="selectedCollection" :action="modalAction" :collection="selectedCollection" />
   <EditCollection v-if="selectedCollection" :action="modalAction" :collection="selectedCollection" />
</template>